/**
 * Kebijakan retensi & arsip tabel log (10 Okt 2026) — SATU sumber kebenaran.
 *
 * Akar masalah yang dicegah: `debugLogs` & `auditLogs` tumbuh tanpa batas —
 * setiap refresh keyboard, setiap attempt LLM, setiap aksi tombol menulis satu
 * baris dan tidak ada jalur yang menghapusnya. Di free tier Convex (0,5 GB)
 * tabel log akhirnya menyaingi data transaksi.
 *
 * Kebijakan (dokumen: SCHEMA.md §Retensi Log, AGENTS.md §7.18):
 *   1. `debugLogs` = diagnostik → dihapus MENTAH setelah
 *      `DEBUG_LOG_RETENTION_DAYS` (insiden lama tetap bisa dibedah sebulan).
 *   2. `auditLogs` = akuntabilitas → TIDAK dihapus mentah. Baris lebih tua dari
 *      `AUDIT_DETAIL_RETENTION_DAYS` DIGULUNG dulu ke `auditLogArchives`
 *      (ringkasan per tenant × hari × aksi: jumlah, waktu pertama/terakhir,
 *      contoh id entitas), BARU dihapus. Inilah kontrak "tanpa menghilangkan
 *      jejak audit": detail baris hilang, jejak "berapa kali & kapan" tetap.
 *   3. `auditLogArchives` = arsip ringkas → dijaga `AUDIT_ARCHIVE_RETENTION_DAYS`.
 *
 * PURE (tanpa import Convex/jaringan) supaya bisa dimuat langsung dari probe
 * `node --experimental-strip-types` SEKALIGUS dipakai dashboard untuk menulis
 * teks kebijakan — angka retensi tidak pernah ada dua salinan. Diuji
 * `scripts/probe-log-retention.ts`.
 */

export const DAY_MS = 24 * 60 * 60 * 1000;

/** Diagnostik bot (`debugLogs`) — cukup sebulan untuk membedah insiden. */
export const DEBUG_LOG_RETENTION_DAYS = 30;
/** Detail audit (`auditLogs`) — dijaga lebih lama, lalu digulung (bukan dibuang). */
export const AUDIT_DETAIL_RETENTION_DAYS = 90;
/** Arsip ringkasan audit (`auditLogArchives`) — kecil, dijaga 2 tahun. */
export const AUDIT_ARCHIVE_RETENTION_DAYS = 730;

/** Baris per batch hapus/gulung (satu transaksi Convex tetap ringan). */
export const LOG_RETENTION_BATCH_SIZE = 100;
/** Batch per kategori per pass — batas kerja satu invokasi cron. */
export const LOG_RETENTION_BATCHES_PER_PASS = 5;
/** Pass lanjutan maksimum (self-schedule) untuk menuntaskan backlog. */
export const LOG_RETENTION_MAX_PASSES = 10;
/** Contoh id entitas per baris arsip — sisanya cukup terwakili `count`. */
export const AUDIT_ARCHIVE_SAMPLE_IDS = 3;

/** Batas waktu retensi: baris dengan `timestamp < cutoff` boleh diproses. */
export function retentionCutoff(now: number, days: number): number {
  return now - days * DAY_MS;
}

/**
 * Kunci hari arsip (UTC). Sengaja UTC, bukan timezone tenant: satu aksi audit
 * bisa menyangkut lintas tenant, dan batas harinya harus deterministik supaya
 * gulungan yang dijalankan ulang tidak memecah/menggandakan grup.
 */
export function utcDayKey(timestamp: number): string {
  return new Date(timestamp).toISOString().slice(0, 10);
}

/** Kunci grup arsip: tenantId, atau `"global"` untuk aksi lintas tenant. */
export function archiveScopeKey(tenantId?: string | null): string {
  return tenantId ?? "global";
}

/** Baris `auditLogs` yang ikut digulung (subset kolom yang dibaca). */
export interface AuditLogRow {
  tenantId?: string | null;
  action: string;
  entityType?: string | null;
  entityId?: string | null;
  timestamp: number;
}

/** Satu baris ringkasan arsip (per tenant × hari × aksi). */
export interface AuditArchiveRow {
  scopeKey: string;
  tenantId?: string | null;
  dayKey: string;
  action: string;
  entityType?: string | null;
  count: number;
  firstAt: number;
  lastAt: number;
  sampleEntityIds: string[];
}

/**
 * Gulung baris audit mentah → ringkasan per `(tenant, hari, aksi)`.
 * Semua baris untuk grup yang sama digabung: `count` ditambah, jendela waktu
 * dilebarkan, `entityType` pertama yang terisi dipakai, dan `entityId`
 * disimpan sebagai contoh maksimal `AUDIT_ARCHIVE_SAMPLE_IDS`.
 */
export function buildAuditArchiveRows(rows: readonly AuditLogRow[]): AuditArchiveRow[] {
  const byKey = new Map<string, AuditArchiveRow>();

  for (const row of rows) {
    const dayKey = utcDayKey(row.timestamp);
    const scopeKey = archiveScopeKey(row.tenantId);
    const key = `${scopeKey}|${dayKey}|${row.action}`;
    const existing = byKey.get(key);

    if (!existing) {
      byKey.set(key, {
        scopeKey,
        tenantId: row.tenantId ?? null,
        dayKey,
        action: row.action,
        entityType: row.entityType ?? null,
        count: 1,
        firstAt: row.timestamp,
        lastAt: row.timestamp,
        sampleEntityIds: row.entityId ? [row.entityId] : [],
      });
      continue;
    }

    existing.count += 1;
    existing.firstAt = Math.min(existing.firstAt, row.timestamp);
    existing.lastAt = Math.max(existing.lastAt, row.timestamp);
    if (!existing.entityType) existing.entityType = row.entityType ?? null;
    if (
      row.entityId &&
      !existing.sampleEntityIds.includes(row.entityId) &&
      existing.sampleEntityIds.length < AUDIT_ARCHIVE_SAMPLE_IDS
    ) {
      existing.sampleEntityIds.push(row.entityId);
    }
  }

  return Array.from(byKey.values());
}

/** Baris arsip yang sudah ada di DB (kolom yang relevan untuk penggabungan). */
export interface ExistingAuditArchive {
  count: number;
  firstAt: number;
  lastAt: number;
  entityType?: string | null;
  sampleEntityIds?: string[];
}

/**
 * Patch untuk baris arsip yang SUDAH ada: jumlah ditambah, jendela waktu
 * dilebarkan, contoh id digabung tanpa duplikat (tetap dibatasi
 * `AUDIT_ARCHIVE_SAMPLE_IDS`) — supaya gulungan beberapa pass/hari tidak
 * menimpa ringkasan sebelumnya.
 */
export function mergeAuditArchive(
  existing: ExistingAuditArchive,
  incoming: AuditArchiveRow,
): {
  entityType: string | null;
  count: number;
  firstAt: number;
  lastAt: number;
  sampleEntityIds: string[];
} {
  const sampleEntityIds = [...(existing.sampleEntityIds ?? [])];
  for (const id of incoming.sampleEntityIds) {
    if (!sampleEntityIds.includes(id) && sampleEntityIds.length < AUDIT_ARCHIVE_SAMPLE_IDS) {
      sampleEntityIds.push(id);
    }
  }
  return {
    entityType: existing.entityType ?? incoming.entityType ?? null,
    count: existing.count + incoming.count,
    firstAt: Math.min(existing.firstAt, incoming.firstAt),
    lastAt: Math.max(existing.lastAt, incoming.lastAt),
    sampleEntityIds,
  };
}
